// src/components/TopicMafia/components/VoteDialog.jsx
import React from "react";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    Typography,
    Divider,
} from "@mui/material";
import { mainActionBtnSx, subActionBtnSx } from "../topicMafiaUi";

export default function VoteDialog({
    open,
    onClose,
    candidates,
    value,
    onChange,
    onSubmit,
    disabledSubmit,
    disabledPick,
    playerNameMap,
    recentSays, // ✅ Room에서 recentSaysForDialog 넘김
    isMobile,   // ✅ Room에서 isMobile 넘김 (중복 선언 금지!)
}) {
    const selectedName =
        value != null
            ? candidates.find((c) => Number(c.id) === Number(value))?.nickname ||
            playerNameMap?.get?.(Number(value)) ||
            `#${value}`
            : null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={!!isMobile}
            fullWidth
            maxWidth={isMobile ? false : "sm"}
        >
            <DialogTitle sx={{ fontWeight: 900 }}>
                투표하기
                {value && (
                    <Chip
                        sx={{ ml: 1, fontWeight: 900 }}
                        label={`선택: ${selectedName}`}
                        color="warning"
                    />
                )}
            </DialogTitle>

            <DialogContent dividers>
                {/* ✅ 후보 리스트 */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 1.2,
                    }}
                >
                    {candidates.map((p) => {
                        const selected = Number(value) === Number(p.id);
                        const locked = !!disabledPick;
                        const dead = !p.is_alive;
                        const name = p.nickname || playerNameMap?.get?.(Number(p.id)) || `#${p.id}`;

                        return (
                            <Paper
                                key={p.id}
                                variant="outlined"
                                onClick={() => {
                                    if (locked || dead) return;
                                    onChange(p.id);
                                }}
                                sx={{
                                    p: 1.4,
                                    borderRadius: 3,
                                    cursor: locked || dead ? "not-allowed" : "pointer",
                                    opacity: locked || dead ? 0.55 : 1,
                                    borderColor: selected ? "warning.main" : "divider",
                                    bgcolor: selected ? "warning.50" : "background.paper",
                                    transition:
                                        "transform 140ms ease, border-color 140ms ease, box-shadow 140ms ease",
                                    "&:hover":
                                        locked || dead
                                            ? {}
                                            : {
                                                transform: "translateY(-2px)",
                                                borderColor: selected ? "warning.main" : "text.secondary",
                                                boxShadow: 2,
                                            },
                                }}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography sx={{ fontWeight: 900 }}>{name}</Typography>
                                    <Chip
                                        size="small"
                                        label={p.is_alive ? "생존" : "탈락"}
                                        variant="outlined"
                                    />
                                </Stack>
                            </Paper>
                        );
                    })}
                </Box>

                {candidates.length === 0 && (
                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                        후보가 없습니다.
                    </Typography>
                )}

                {/* ✅ 타임라인 (최근 발언) */}
                <Divider sx={{ my: 2 }} />

                <Typography sx={{ fontWeight: 900, mb: 1 }}>
                    🧾 타임라인 (최근 발언)
                </Typography>

                <Box
                    sx={{
                        maxHeight: isMobile ? "32vh" : 220,
                        overflow: "auto",
                        pr: 0.5,
                        borderRadius: 2,
                    }}
                >
                    <Stack spacing={1}>
                        {!recentSays || recentSays.length === 0 ? (
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                아직 발언이 없습니다.
                            </Typography>
                        ) : (
                            recentSays.map((s, idx) => {
                                const displayName =
                                    s.nickname ||
                                    playerNameMap?.get?.(Number(s.player_id)) ||
                                    `#${s.player_id}`;

                                // created_at이 ISO 문자열이면 HH:mm:ss만 잘라서 표시
                                const timeText =
                                    typeof s.created_at === "string" && s.created_at.length >= 19
                                        ? s.created_at.slice(11, 19)
                                        : "";

                                return (
                                    <Paper
                                        key={s.id || `vote-dlg-say-${idx}`}
                                        variant="outlined"
                                        sx={{ p: 1.2, borderRadius: 2 }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{ fontWeight: 900, color: "text.secondary" }}
                                        >
                                            {displayName}
                                            {timeText ? ` · ${timeText}` : ""}
                                        </Typography>
                                        <Typography
                                            sx={{ fontWeight: 800, whiteSpace: "pre-wrap", mt: 0.3 }}
                                        >
                                            {s.text}
                                        </Typography>
                                    </Paper>
                                );
                            })
                        )}
                    </Stack>
                </Box>
            </DialogContent>

            <DialogActions
                sx={
                    isMobile
                        ? { position: "sticky", bottom: 0, bgcolor: "background.paper", zIndex: 1 }
                        : undefined
                }
            >
                <Button onClick={onClose} sx={subActionBtnSx} fullWidth={!!isMobile}>
                    닫기
                </Button>

                <Button
                    variant="contained"
                    onClick={onSubmit}
                    disabled={!value || disabledSubmit}
                    sx={mainActionBtnSx}
                    fullWidth={!!isMobile}
                >
                    {value ? `${playerNameMap?.get?.(Number(value)) || `#${value}`} 에게 투표` : "제출"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
